/**
 * Widget styles, injected into the shadow root. Two palettes; the dark
 * one is the design reference (specs/support-chat.md, canvas "Final").
 */
export const styles = `
:host { all: initial; }
*, *::before, *::after { box-sizing: border-box; }

.ps {
  --bg: #22262d;
  --bg-2: #2a2e36;
  --bg-3: #353a43;
  --line: #424751;
  --line-2: #565b66;
  --ink: #ece7dc;
  --ink-2: #bdb6a4;
  --ink-3: #8f8878;
  --head: #f3ecdc;
  --gold: #e0b45a;
  --gold-2: #a8863e;
  --gold-ink: #1a1408;
  --ok: #21a179;
  --rim: rgba(255,255,255,0.22);
  --shadow: 0 0 0 1px var(--rim), 0 30px 60px -20px rgba(0,0,0,0.85), 0 0 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08);
  --launcher-shadow: 0 0 0 1px var(--rim), 0 12px 28px -10px rgba(0,0,0,0.7), 0 0 24px rgba(0,0,0,0.5);
  --serif: 'Fraunces', Georgia, 'Times New Roman', serif;
  --sans: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-family: var(--sans);
  color: var(--ink);
  font-size: 14px;
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}
.ps[data-theme="light"] {
  --bg: #f8f3e8;
  --bg-2: #ffffff;
  --bg-3: #ffffff;
  --line: #e3d8c2;
  --line-2: #d4c7ac;
  --ink: #2a2419;
  --ink-2: #5f5546;
  --ink-3: #8a7f6e;
  --head: #2a2419;
  --gold: #c9962e;
  --gold-2: #e0b45a;
  --gold-ink: #1a1408;
  --rim: rgba(60,45,20,0.12);
  --shadow: 0 0 0 1px var(--rim), 0 24px 48px -16px rgba(80,60,20,0.3);
  --launcher-shadow: 0 0 0 1px var(--rim), 0 12px 28px -10px rgba(80,60,20,0.35);
}

button { font: inherit; color: inherit; background: none; border: 0; padding: 0; cursor: pointer; }
img { display: block; }

/* Launcher */
.ps-launcher {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483000;
  display: inline-flex; align-items: center; gap: 10px;
  padding: 8px 16px 8px 8px; border-radius: 999px;
  background: var(--bg); border: 1px solid var(--line-2); box-shadow: var(--launcher-shadow);
  transition: transform .15s ease;
}
.ps-launcher:hover { transform: translateY(-1px); }
.ps-launcher:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.ps-launcher .ps-faces { display: flex; }
.ps-launcher .ps-faces img {
  width: 30px; height: 30px; border-radius: 999px; object-fit: cover;
  border: 1.5px solid var(--gold); box-shadow: 0 0 0 2px var(--bg);
}
.ps-launcher .ps-faces img + img { margin-left: -9px; }
.ps-launcher .ps-label { font-family: var(--serif); font-style: italic; font-size: 15px; color: var(--head); white-space: nowrap; }
.ps-launcher.ps-compact { padding: 0; width: 44px; height: 44px; justify-content: center; }
.ps-launcher.ps-compact .ps-faces img { width: 34px; height: 34px; box-shadow: none; }
.ps-launcher.ps-compact .ps-chat {
  position: absolute; right: -3px; bottom: -3px; width: 18px; height: 18px; border-radius: 999px;
  background: var(--gold); border: 2px solid #fff; display: flex; align-items: center; justify-content: center;
}
.ps-badge {
  position: absolute; top: -6px; right: -6px; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px;
  background: var(--gold); color: var(--gold-ink); font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; border: 2px solid var(--bg);
}
.ps-launcher[hidden] { display: none; }

/* Panel */
.ps-panel {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483001;
  width: 380px; max-width: calc(100vw - 40px); max-height: min(720px, calc(100vh - 40px));
  display: flex; flex-direction: column; overflow: hidden;
  background: var(--bg); border: 1px solid var(--line); border-radius: 22px; box-shadow: var(--shadow);
  transform-origin: bottom right;
  animation: ps-in .18s ease-out;
}
@keyframes ps-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
.ps-panel[hidden] { display: none; }
@media (max-width: 480px) {
  .ps-panel { right: 0; bottom: 0; width: 100vw; max-width: 100vw; height: 100vh; height: 100dvh; max-height: 100vh; max-height: 100dvh; border-radius: 0; border: 0; }
  /* iOS Safari zooms the page on inputs smaller than 16px. */
  .ps-box textarea, .ps-email-row input { font-size: 16px; }
  .ps-launcher:not(.ps-compact) { padding: 6px; right: 16px; bottom: 16px; }
  .ps-launcher:not(.ps-compact) .ps-label { display: none; }
  .ps-launcher:not(.ps-compact) .ps-faces img { width: 34px; height: 34px; }
}

.ps-close {
  position: absolute; top: 12px; right: 12px; width: 32px; height: 32px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center; color: var(--ink-2);
}
.ps-close:hover { background: var(--bg-3); color: var(--ink); }

.ps-head {
  padding: 24px 22px 14px; display: flex; flex-direction: column; gap: 12px; flex-shrink: 0;
  background: radial-gradient(120% 90% at 50% 0%, rgba(224,180,90,0.38), rgba(224,180,90,0.08) 45%, rgba(224,180,90,0) 75%);
}
.ps-head .ps-faces { display: flex; justify-content: center; }
.ps-head .ps-faces img {
  width: 40px; height: 40px; border-radius: 999px; object-fit: cover;
  border: 2px solid var(--gold); box-shadow: 0 0 0 3px var(--bg);
}
.ps-head .ps-faces img + img { margin-left: -8px; }
.ps-head.ps-away .ps-faces img { border-color: var(--line-2); filter: saturate(.4) brightness(.85); }
.ps-title { font-family: var(--serif); font-style: italic; font-weight: 400; font-size: 24px; line-height: 1.1; color: var(--head); text-align: center; letter-spacing: -0.01em; margin: 0; }
.ps-sub { font-size: 13px; line-height: 1.45; color: var(--ink-2); text-align: center; margin: 0; }
.ps-sub .ps-dot { display: inline-block; width: 7px; height: 7px; border-radius: 999px; background: var(--ok); margin-right: 6px; vertical-align: 1px; box-shadow: 0 0 0 3px rgba(33,161,121,0.18); }
.ps-sub b { color: var(--ink); font-weight: 600; }

/* Thread */
.ps-thread {
  flex: 1 1 auto; min-height: 120px; overflow-y: auto; overscroll-behavior: contain;
  padding: 12px 16px 16px; display: flex; flex-direction: column; gap: 14px;
  border-top: 1px solid var(--line);
}
.ps-thread::-webkit-scrollbar { width: 8px; }
.ps-thread::-webkit-scrollbar-thumb { background: var(--line-2); border-radius: 8px; }
.ps-empty { font-size: 14px; color: var(--ink-2); text-align: center; padding: 28px 16px; line-height: 1.5; }
.ps-row { display: flex; gap: 10px; align-items: flex-end; }
.ps-row.ps-visitor { justify-content: flex-end; }
.ps-avatar { width: 26px; height: 26px; border-radius: 999px; object-fit: cover; flex-shrink: 0; border: 1.5px solid var(--gold); }
.ps-col { display: flex; flex-direction: column; gap: 5px; max-width: 82%; }
.ps-bubble { padding: 10px 14px; border-radius: 16px 16px 16px 4px; background: var(--bg-3); border: 1px solid var(--line-2); color: var(--ink); white-space: pre-wrap; word-wrap: break-word; }
.ps-visitor .ps-bubble { border-radius: 16px 16px 4px 16px; background: var(--gold); border-color: var(--gold); color: var(--gold-ink); }
.ps-bubble a { color: inherit; text-decoration: underline; }
.ps-meta { font-size: 12px; color: var(--ink-3); padding-left: 4px; display: flex; gap: 6px; align-items: center; }
.ps-visitor .ps-meta { padding-left: 0; padding-right: 4px; justify-content: flex-end; }
.ps-meta i { font-family: var(--serif); font-style: italic; color: var(--gold); }
.ps-images { display: flex; flex-wrap: wrap; gap: 6px; }
.ps-images a { display: block; border-radius: 10px; overflow: hidden; border: 1px solid var(--line); }
.ps-images img { max-width: 180px; max-height: 140px; object-fit: cover; }
.ps-sending .ps-bubble { opacity: .6; }
.ps-failed .ps-meta { color: #e07a5f; }

.ps-card { display: flex; gap: 10px; align-items: flex-start; }
.ps-card .ps-faces { display: flex; flex-shrink: 0; margin-top: 2px; }
.ps-card .ps-faces img { width: 26px; height: 26px; border-radius: 999px; object-fit: cover; border: 1.5px solid var(--gold); box-shadow: 0 0 0 2px var(--bg); }
.ps-card .ps-faces img + img { margin-left: -8px; }
.ps-card-body { display: flex; flex-direction: column; gap: 10px; max-width: 84%; padding: 12px 14px; border-radius: 4px 16px 16px 16px; background: var(--bg-3); border: 1px solid var(--line); }
.ps-card-body p { margin: 0; font-size: 13.5px; }
.ps-email { display: flex; flex-direction: column; gap: 6px; }
.ps-email-row { display: flex; gap: 6px; }
.ps-email-row input {
  flex: 1; min-width: 0; height: 36px; border-radius: 10px; border: 1px solid var(--line-2); background: var(--bg);
  color: var(--ink); font: inherit; font-size: 13px; padding: 0 10px;
}
.ps-email-row input::placeholder { color: var(--ink-3); }
.ps-email-row input:focus { outline: none; border-color: var(--gold); }
.ps-email-row button { height: 36px; padding: 0 12px; border-radius: 10px; background: var(--gold); color: var(--gold-ink); font-size: 12.5px; font-weight: 600; white-space: nowrap; }
.ps-email-row button:disabled { opacity: .5; cursor: default; }
.ps-hint { font-size: 11px; color: var(--gold); }
.ps-error { font-size: 11px; color: #e07a5f; }

.ps-line { display: flex; justify-content: center; }
.ps-line span { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--ink-3); text-align: center; }
.ps-divider { display: flex; align-items: center; gap: 10px; }
.ps-divider::before, .ps-divider::after { content: ""; flex: 1; height: 1px; background: rgba(224,180,90,0.35); }
.ps-divider span { font-size: 11px; color: var(--gold); font-weight: 500; }

/* Composer */
.ps-composer { padding: 12px 16px 16px; border-top: 1px solid var(--line); flex-shrink: 0; }
.ps-box { border-radius: 16px; border: 1px solid var(--line); background: var(--bg-2); padding: 8px 8px 8px 14px; display: flex; flex-direction: column; gap: 6px; }
.ps-box:focus-within { border-color: var(--line-2); }
.ps-box textarea {
  width: 100%; resize: none; border: 0; background: transparent; color: var(--ink); font: inherit; font-size: 14px;
  line-height: 1.45; padding: 6px 0 0; min-height: 28px; max-height: 120px; outline: none;
}
.ps-box textarea::placeholder { color: var(--ink-3); }
.ps-tools { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ps-attach { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: var(--ink-2); }
.ps-attach:hover { background: var(--bg-3); color: var(--ink); }
.ps-send { height: 34px; padding: 0 14px; border-radius: 999px; background: var(--gold); color: var(--gold-ink); font-size: 13.5px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
.ps-send:disabled { opacity: .45; cursor: default; }
.ps-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ps-chip { position: relative; width: 56px; height: 56px; border-radius: 10px; overflow: hidden; border: 1px solid var(--line-2); background: var(--bg); }
.ps-chip img { width: 100%; height: 100%; object-fit: cover; }
.ps-chip.ps-uploading img { opacity: .4; }
.ps-chip button { position: absolute; top: 2px; right: 2px; width: 18px; height: 18px; border-radius: 999px; background: rgba(0,0,0,.7); color: #fff; font-size: 12px; line-height: 1; display: flex; align-items: center; justify-content: center; }
.ps-foot { font-size: 11.5px; color: var(--ink-3); text-align: center; padding-top: 10px; }
.ps-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
`;
